import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import {
  Restore as RestoreIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Upload as UploadIcon,
  Warning as WarningIcon,
  Storage as StorageIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/axios';

interface RestoreResult {
  success: boolean;
  message: string;
  restoredTables?: string[];
  restoredFiles?: number;
}

const RestorePage: React.FC = () => {
  const { user } = useAuth();
  const [restoring, setRestoring] = useState(false);
  const [result, setResult] = useState<RestoreResult | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [availableBackups, setAvailableBackups] = useState<any[]>([]);

  const handleFileSelect = (file: File | null) => {
    setSelectedFile(file);
  };

  const handleRestore = async () => {
    if (!selectedFile) return;

    setShowConfirmDialog(true);
  };

  const confirmRestore = async () => {
    setShowConfirmDialog(false);
    setRestoring(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('backup', selectedFile);

      const response = await api.post('/backup/restore', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setResult({
        success: true,
        message: 'Restore completed successfully',
        restoredTables: response.data.restoredTables,
        restoredFiles: response.data.restoredFiles
      });

      // Refresh available backups list
      fetchAvailableBackups();
    } catch (error: any) {
      setResult({
        success: false,
        message: error.response?.data?.message || 'Restore failed'
      });
    }

    setRestoring(false);
  };

  const fetchAvailableBackups = async () => {
    try {
      const response = await api.get('/backup/list');
      setAvailableBackups(response.data.backups || []);
    } catch (error) {
      console.error('Failed to fetch available backups:', error);
    }
  };

  const handleRestoreFromBackup = async (backupId: string) => {
    setShowConfirmDialog(true);
    // Store the backup ID for restoration
    setSelectedFile({ name: `backup_${backupId}.zip`, size: 0 } as File);
  };

  React.useEffect(() => {
    fetchAvailableBackups();
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Database Restore
      </Typography>
      
      <Alert severity="warning" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Warning:</strong> Restoring a backup will completely replace your current database and files. 
          This action cannot be undone. Make sure you have a current backup before proceeding.
        </Typography>
      </Alert>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Restore from Backup File
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Upload a backup file to restore your database:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <StorageIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Database tables and data" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <UploadIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Uploaded files and attachments" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <WarningIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Current data will be replaced" />
                  </ListItem>
                </List>
              </Box>

              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadIcon />}
                disabled={restoring}
                fullWidth
                sx={{ mb: 2 }}
              >
                {selectedFile ? selectedFile.name : 'Choose Backup File'}
                <input
                  type="file"
                  hidden
                  accept=".zip,.backup"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    handleFileSelect(file);
                  }}
                />
              </Button>

              <Button
                variant="contained"
                onClick={handleRestore}
                disabled={!selectedFile || restoring}
                startIcon={restoring ? <CircularProgress size={20} /> : <RestoreIcon />}
                fullWidth
                size="large"
                color="warning"
              >
                {restoring ? 'Restoring...' : 'Restore Database'}
              </Button>
            </CardContent>
          </Card>

          {result && (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Restore Result
                </Typography>
                
                <Paper sx={{ p: 2, border: 1, borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    {result.success ? (
                      <SuccessIcon color="success" />
                    ) : (
                      <ErrorIcon color="error" />
                    )}
                    <Typography variant="subtitle2">
                      {result.success ? 'Restore Successful' : 'Restore Failed'}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {result.message}
                  </Typography>
                  {result.success && result.restoredTables && (
                    <Typography variant="body2" color="text.secondary">
                      Restored tables: {result.restoredTables.join(', ')}
                    </Typography>
                  )}
                  {result.success && result.restoredFiles !== undefined && (
                    <Typography variant="body2" color="text.secondary">
                      Restored files: {result.restoredFiles}
                    </Typography>
                  )}
                </Paper>
              </CardContent>
            </Card>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Available Backups
              </Typography>
              
              {availableBackups.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No backup files available for restore.
                </Typography>
              ) : (
                <List>
                  {availableBackups.map((backup, index) => (
                    <React.Fragment key={backup.id}>
                      <ListItem>
                        <ListItemIcon>
                          <RestoreIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={`Backup ${backup.id}`}
                          secondary={
                            <Box>
                              <Typography variant="body2">
                                {new Date(backup.createdAt).toLocaleString()}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Size: {backup.size || 'Unknown'}
                              </Typography>
                            </Box>
                          }
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          color="warning"
                          startIcon={<RestoreIcon />}
                          onClick={() => handleRestoreFromBackup(backup.id)}
                          disabled={restoring}
                        >
                          Restore
                        </Button>
                      </ListItem>
                      {index < availableBackups.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        aria-labelledby="restore-confirm-dialog-title"
        aria-describedby="restore-confirm-dialog-description"
      >
        <DialogTitle id="restore-confirm-dialog-title">
          Confirm Database Restore
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="restore-confirm-dialog-description">
            <Alert severity="error" sx={{ mb: 2 }}>
              <strong>Warning:</strong> This action will completely replace your current database and files.
              All existing data will be lost and cannot be recovered.
            </Alert>
            Are you absolutely sure you want to proceed with the restore?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmRestore} color="warning" variant="contained" autoFocus>
            Yes, Restore Database
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RestorePage;
